import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { LegislationVersion } from '@/lib/legislationVersionClient';
import { useTranslations } from 'next-intl';

interface VersionSelectorProps {
  versions: LegislationVersion[];
  selectedVersion: string;
  onVersionChange: (version: string) => void;
  isLoading: boolean;
  currentVersionName: string;
}

const VersionSelector = ({
  versions,
  selectedVersion,
  onVersionChange,
  isLoading,
  currentVersionName,
}: VersionSelectorProps) => {
  const t = useTranslations('documentVersionComparison');
  const tCommon = useTranslations('Common');

  const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr === 'Vyhlásené znenie' || dateStr === '')
      return dateStr;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('sk-SK');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className='flex flex-col md:flex-row gap-4 py-4 border-b border-line-default'>
      <div className='w-full md:w-1/2'>
        <label className='block text-sm font-medium text-content-secondary mb-2'>
          {t('version1')}
        </label>
        <div className='flex items-center h-10 px-3 rounded-md border border-input bg-surface-subtle text-sm text-content-primary'>
          <span className='truncate'>{currentVersionName}</span>
        </div>
      </div>

      <div className='w-full md:w-1/2'>
        <label className='block text-sm font-medium text-content-secondary mb-2'>
          {t('version2')}
        </label>
        <Select
          value={selectedVersion}
          onValueChange={onVersionChange}
          disabled={isLoading || versions.length === 0}
        >
          <SelectTrigger className='h-10'>
            <SelectValue placeholder={tCommon('selectVersion')} />
          </SelectTrigger>
          <SelectContent className='z-[2200]'>
            {versions.length === 0 && isLoading ? (
              <div className='flex items-center justify-center p-2'>
                <svg
                  className='animate-spin h-4 w-4 text-status-info mr-2'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  />
                </svg>
                <span>{t('loadingVersions')}</span>
              </div>
            ) : versions.length === 0 ? (
              <div className='p-2 text-center text-content-muted'>
                {t('noVersionsAvailable')}
              </div>
            ) : (
              versions.map((version) => (
                <SelectItem key={version.iri} value={version.iri}>
                  {version.caption ||
                    formatDate(version.ucinnyOd) +
                      (version.ucinnyDo &&
                      version.ucinnyDo !== 'Vyhlásené znenie' &&
                      version.ucinnyDo !== ''
                        ? ` - ${formatDate(version.ucinnyDo)}`
                        : '') ||
                    'Vyhlásené znenie'}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default VersionSelector;
